import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
} from "recharts";
import { Search, Cpu, PenTool, Send, ShieldCheck } from "lucide-react";

const LEVEL_COLORS = {
  1: "text-slate-400 border-slate-600 bg-slate-800/50",
  2: "text-sky-400 border-sky-500/40 bg-sky-500/10",
  3: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
  4: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
  5: "text-amber-400 border-amber-500/40 bg-amber-500/10",
};

export function MaturityBadge({ maturity }) {
  if (!maturity) return null;
  const cls = LEVEL_COLORS[maturity.level] || LEVEL_COLORS[1];
  return (
    <span
      data-testid="maturity-badge"
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${cls}`}
    >
      <span className="font-mono font-bold">N{maturity.level}</span>
      {maturity.label}
    </span>
  );
}

export function IndexRing({ value, size = 132 }) {
  const pct = Math.max(0, Math.min(1, value / 5));
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const color = value >= 4 ? "#f59e0b" : value >= 3 ? "#10b981" : value >= 2 ? "#22d3ee" : "#64748b";
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="index-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1e293b" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 1s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-3xl font-bold" style={{ color }}>
          {value.toFixed(2)}
        </span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">/ 5.00</span>
      </div>
    </div>
  );
}

export function ScoreRadar({ dimensions }) {
  const data = (dimensions || []).map((d) => ({
    dim: d.name.split(" ")[0],
    fullName: d.name,
    score: d.score,
  }));
  return (
    <div className="h-[340px] w-full" data-testid="score-radar">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="#1e293b" />
          <PolarAngleAxis dataKey="dim" tick={{ fill: "#94a3b8", fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 5]} tick={{ fill: "#475569", fontSize: 9 }} axisLine={false} />
          <Radar dataKey="score" stroke="#22d3ee" fill="#22d3ee" fillOpacity={0.25} strokeWidth={2} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

const MAP_LAYERS = [
  { key: "input", label: "Entrada", icon: Search, color: "text-sky-400" },
  { key: "processing", label: "Procesamiento", icon: Cpu, color: "text-cyan-400" },
  { key: "production", label: "Producción", icon: PenTool, color: "text-violet-400" },
  { key: "execution", label: "Ejecución", icon: Send, color: "text-emerald-400" },
  { key: "control", label: "Control", icon: ShieldCheck, color: "text-amber-400" },
];

export function AgenticMap({ map }) {
  if (!map) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="agentic-map">
      {MAP_LAYERS.map((layer, i) => {
        const items = map[layer.key] || [];
        return (
          <div key={layer.key} className="relative rounded-lg border border-slate-800 bg-slate-950/50 p-4">
            <div className={`flex items-center gap-2 mb-3 ${layer.color}`}>
              <layer.icon size={16} strokeWidth={1.5} />
              <span className="text-xs font-semibold uppercase tracking-wider">{layer.label}</span>
            </div>
            <div className="space-y-2">
              {items.length === 0 && <p className="text-xs text-slate-600 italic">Sin agentes</p>}
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="rounded border border-slate-800 bg-slate-900 px-2.5 py-1.5 text-xs text-slate-300"
                >
                  {it}
                </div>
              ))}
            </div>
            {i < MAP_LAYERS.length - 1 && (
              <div className="hidden md:block absolute top-1/2 -right-2 text-slate-700 z-10">›</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
